import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { BadgeCheck, Flag, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const CLASSES = ["Formula 500s", "Wingless Sprints", "Street Stocks", "Super Rods", "Junior Quarter Midgets / JQMV", "Production Sedans", "Standard Saloons", "Compact Speedcars", "Lightning Sprints", "Other"];
const BODIES = ["Speedway Australia", "AASA", "Other"];

const nomClosed = (e) =>
  e.cancelled || e.nominations_open === false || (e.nomination_cutoff && new Date(e.nomination_cutoff) < new Date());

// Classes racing at the selected meeting (from its calendar listing), always ending with one Other
const classesFor = (ev) => {
  const listed = (ev?.classes || "")
    .split("·")
    .map((c) => c.trim())
    .filter((c) => c && !/more classes/i.test(c) && c.toLowerCase() !== "other");
  const base = listed.length ? listed : CLASSES.filter((c) => c !== "Other");
  return [...base, "Other"];
};

const EMPTY = {
  event_id: "",
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  car_number: "",
  racing_class: "Street Stocks",
  transponder: "",
  sanctioning_body: "Speedway Australia",
  licence_no: "",
  pit_crew: "",
  notes: "",
};

const inputCls =
  "w-full bg-clay-bg border border-clay-border focus:border-clay-orange outline-none px-4 py-3 text-clay-text placeholder:text-clay-muted/60 transition-colors duration-300 font-sans";

export default function NominateModal({ open, setOpen, preselect }) {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!open) return;
    setDone(null);
    api.get("/events").then(({ data }) => {
      const now = new Date();
      const upcoming = (Array.isArray(data) ? data : []).filter((e) => new Date(`${e.date}T23:59:59`) >= now);
      setEvents(upcoming.length ? upcoming : data);
      setForm((f) => ({ ...EMPTY, event_id: preselect || (upcoming[0] || data[0] || {}).id || "" }));
    }).catch(() => toast.error("Could not load race meetings"));
  }, [open, preselect]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const selectedEvent = events.find((e) => e.id === form.event_id);
  const classOptions = classesFor(selectedEvent);

  // keep the chosen class valid for the selected meeting
  useEffect(() => {
    if (!classOptions.includes(form.racing_class)) {
      setForm((f) => ({ ...f, racing_class: classOptions[0] }));
    }
  }, [form.event_id, events]);
  const fullName = `${form.first_name.trim()} ${form.last_name.trim()}`.trim();
  const valid = form.event_id && !(selectedEvent && nomClosed(selectedEvent)) && form.first_name.trim() && form.last_name.trim() && /.+@.+\..+/.test(form.email) && form.car_number.trim();

  const close = () => setOpen(false);

  const submit = async () => {
    setLoading(true);
    try {
      const { first_name, last_name, ...rest } = form;
      const { data } = await api.post("/nominations", { ...rest, driver_name: fullName });
      setDone(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="nomination-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
          onClick={close}
        >
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-xl bg-clay-surface border border-clay-border p-6 sm:p-10 max-h-[92vh] overflow-y-auto"
          >
            <div className="flex items-start justify-between mb-8">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-clay-orange mb-2 flex items-center gap-2">
                  <Flag className="w-3.5 h-3.5" /> Race Nomination
                </p>
                <h3 className="font-display font-bold uppercase text-3xl tracking-tight">
                  {done ? "Nomination In" : "Nominate Now"}
                </h3>
              </div>
              <button data-testid="nomination-close-button" onClick={close} className="text-clay-muted hover:text-clay-text transition-colors">
                <X className="w-6 h-6" />
              </button>
            </div>

            {done ? (
              <div className="text-center space-y-5 py-4">
                <BadgeCheck className="w-14 h-14 text-clay-gold mx-auto" />
                <p className="text-clay-text text-lg">
                  You're nominated for <span className="text-clay-gold font-bold">{done.event_title || "the meeting"}</span>.
                </p>
                <p className="text-clay-muted text-sm max-w-sm mx-auto">
                  The club has your details. If anything changes — car, class or crew — just submit again or let us know at the track.
                </p>
                <button
                  data-testid="nomination-done-button"
                  onClick={close}
                  className="border border-clay-border hover:border-clay-orange font-display font-bold uppercase tracking-wider px-8 py-3 transition-colors duration-300"
                >
                  Done
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay-muted mb-2">Race Meeting *</p>
                  <select data-testid="nomination-event-select" value={form.event_id} onChange={set("event_id")} className={inputCls}>
                    {events.map((e) => (
                      <option key={e.id} value={e.id} disabled={nomClosed(e)}>
                        {new Date(`${e.date}T00:00:00`).toLocaleDateString("en-AU", { day: "2-digit", month: "short" })} — {e.title}
                        {nomClosed(e) ? (e.cancelled ? " (cancelled)" : " (nominations closed)") : ""}
                      </option>
                    ))}
                  </select>
                  {form.event_id && events.find((e) => e.id === form.event_id && nomClosed(e)) && (
                    <p data-testid="nomination-closed-note" className="font-mono text-[11px] uppercase tracking-[0.2em] text-red-400 mt-2">
                      Nominations are closed for this meeting — contact the club to late-enter
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input data-testid="nomination-first-name-input" placeholder="First name *" value={form.first_name} onChange={set("first_name")} className={inputCls} />
                  <input data-testid="nomination-last-name-input" placeholder="Last name *" value={form.last_name} onChange={set("last_name")} className={inputCls} />
                  <input data-testid="nomination-email-input" type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={inputCls} />
                  <input data-testid="nomination-phone-input" placeholder="Phone" value={form.phone} onChange={set("phone")} className={inputCls} />
                  <input data-testid="nomination-car-input" placeholder="Car number *" value={form.car_number} onChange={set("car_number")} className={inputCls} />
                  <select data-testid="nomination-class-select" value={form.racing_class} onChange={set("racing_class")} className={inputCls}>
                    {classOptions.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  <input data-testid="nomination-transponder-input" placeholder="Transponder no. (optional)" value={form.transponder} onChange={set("transponder")} className={inputCls} />
                  <select data-testid="nomination-body-select" value={form.sanctioning_body} onChange={set("sanctioning_body")} className={inputCls}>
                    {BODIES.map((b) => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                  <input data-testid="nomination-licence-input" placeholder="Licence / insurance no." value={form.licence_no} onChange={set("licence_no")} className={inputCls} />
                </div>
                <input data-testid="nomination-crew-input" placeholder="Pit crew names (optional)" value={form.pit_crew} onChange={set("pit_crew")} className={inputCls} />
                <textarea
                  data-testid="nomination-notes-input"
                  placeholder="Notes for the club (optional)"
                  value={form.notes}
                  onChange={set("notes")}
                  rows={2}
                  className={`${inputCls} resize-none`}
                />
                <button
                  data-testid="nomination-submit-button"
                  disabled={!valid || loading}
                  onClick={submit}
                  className="w-full bg-clay-orange hover:bg-clay-gold disabled:opacity-40 disabled:cursor-not-allowed text-clay-bg font-display font-bold uppercase tracking-wider text-lg px-8 py-4 transition-all duration-300 flex items-center justify-center gap-2 clay-glow"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Flag className="w-5 h-5" />}
                  {loading ? "Submitting…" : "Submit Nomination"}
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
